import { ArrowRight, Pill, TriangleAlert } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';
import { DashCard, ViewAllButton, fmtDay } from './DashCard';
import type { InventoryItem } from './types';

export const isExpired = (i: InventoryItem, today: string) => !!i.expiry_date && i.expiry_date.slice(0, 10) < today;
// is_low comes from the server: the store's reorder level when set, else the batch threshold.
export const isLow = (i: InventoryItem) => i.is_low ?? i.quantity_available <= i.low_stock_threshold;

/** "12 strips left": base units divided by the receipt's pack size when known, else plain units. */
function leftLabel(i: InventoryItem): string {
  if (i.units_per_pack && i.units_per_pack > 0) {
    const packs = Math.floor(i.quantity_available / i.units_per_pack);
    const unit = /tablet|capsule/i.test(i.dosage_form ?? '') ? 'strip' : 'pack';
    return `${packs} ${unit}${packs === 1 ? '' : 's'} left`;
  }
  return `${i.quantity_available} unit${i.quantity_available === 1 ? '' : 's'} left`;
}

/**
 * Stock Alerts — docs/design/dashboard region 9: expired batches first (red,
 * tinted row), then lowest stock first, three rows max — "View all" and the
 * footer link go to Pharmacy for the rest.
 */
export function StockAlertsCard({ alerts, today, onOpenStock }: { alerts: InventoryItem[]; today: string; onOpenStock: () => void }) {
  const sorted = [...alerts].sort((a, b) => {
    const ea = isExpired(a, today), eb = isExpired(b, today);
    if (ea !== eb) return ea ? -1 : 1;
    if (ea) return (a.expiry_date ?? '').localeCompare(b.expiry_date ?? '');
    return a.quantity_available - b.quantity_available;
  });
  return (
    <DashCard title="Stock Alerts" action={<ViewAllButton onClick={onOpenStock} />}>
      {sorted.length === 0 ? (
        <div className="flex flex-1 items-center justify-center py-6 text-[13px] text-ink-muted">All items adequately stocked ✓</div>
      ) : (
        <div className="mb-1.5 mt-[15px] flex flex-col gap-[3px]">
          {sorted.slice(0, 3).map(i => {
            const expired = isExpired(i, today);
            return (
              <div key={i.id} className={cn('grid h-[57px] grid-cols-[30px_minmax(0,1fr)_auto] items-center rounded-md border px-2.5', expired ? 'border-danger/30 bg-danger-soft/50' : 'border-line')}>
                {expired ? <TriangleAlert className="h-4 w-4 text-danger" /> : <Pill className="h-4 w-4 text-ink-muted" />}
                <div className="min-w-0">
                  <div className="truncate text-[11.5px] font-medium">{i.generic_name}{i.strength ? ` ${i.strength}` : ''}</div>
                  <div className="mt-1 truncate text-[10px] text-ink-muted">{i.dosage_form ?? ''}</div>
                </div>
                <div className="pl-2 text-right">
                  <Badge variant={expired ? 'danger' : 'warn'} className="rounded px-1.5 py-0 text-[9.5px] font-medium">{expired ? '⚠ Expired' : 'Low stock'}</Badge>
                  <div className="mt-1.5 text-[9.5px] text-ink-muted">{expired && i.expiry_date ? `Expired on ${fmtDay(i.expiry_date)}` : leftLabel(i)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <button
        type="button"
        onClick={onOpenStock}
        className="mt-auto flex h-[30px] w-full items-center justify-center gap-2 rounded-md border border-line text-[11px] font-medium text-teal transition-colors hover:bg-muted/60"
      >
        Go to stock management <ArrowRight className="h-3.5 w-3.5" />
      </button>
    </DashCard>
  );
}
